import { PriorityHigh } from "@mui/icons-material";
import {
    PendingLeaderboardItemContainer,
    PendingLeaderboardActionItem,
} from "components/events/judging/pending/PendingLeaderboardItemView";
import { scrollLeaderboardItemIntoView } from "components/events/judging/pending/scrollLeaderboardItemIntoView";
import { PendingLeaderboardFields } from "components/events/judging/pending/usePendingLeaderboard";
import { usePendingResultChanges } from "components/events/judging/pending/usePendingResultChanges";
import { useEditRideDialogContext } from "components/events/judging/useEditRideDialogContext/useEditRideDialogContext";
import { T } from "components/util/t";
import { HeatResult } from "graphql/generated/types";
import { forwardRef } from "react";

type PendingLeaderboardRowProps = {
    result: PendingLeaderboardFields["result"][number];
    pendingResult: PendingLeaderboardFields["result"][number];
    heat: Pick<PendingLeaderboardFields, "config" | "heats">;
    competitor: PendingLeaderboardFields["competitors"][number];
    tied?: "first" | "last" | boolean;
    isActionItem?: boolean;
    pendingRide?: HeatResult["rides"][number];
    isPendingTie?: boolean;
};

export const PendingLeaderboardRow = forwardRef<
    HTMLLIElement,
    PendingLeaderboardRowProps
>(
    (
        {
            result,
            pendingResult,
            heat,
            competitor,
            tied,
            isActionItem,
            pendingRide,
            isPendingTie,
        },
        ref,
    ) => {
        const { place, formattedTotal, modifier } = usePendingResultChanges(
            result,
            pendingResult,
            heat.config,
        );
        const { triggerOpenDialog } = useEditRideDialogContext();
        const showPlaceCaption = pendingRide && place.change;
        const showTotalCaption =
            pendingRide && formattedTotal.pending !== formattedTotal.approved;

        const caption = pendingRide ? getCaption(place) : "";
        const jersey = heat.config.jerseyOrder?.[competitor.position];
        const hideNames = heat.config.hideNamesFromJudges;

        const PendingLeaderboardItemVariant = isActionItem
            ? PendingLeaderboardActionItem
            : PendingLeaderboardItemContainer;
        const openApprovalDialog = isActionItem
            ? () =>
                  scrollLeaderboardItemIntoView(
                      competitor.athleteId,
                      place.pending,
                  )
            : pendingRide
              ? () =>
                    triggerOpenDialog(
                        getCorrectedRideIndex(pendingRide, heat),
                        competitor.athleteId,
                    )
              : undefined;

        return (
            <PendingLeaderboardItemVariant
                ref={ref}
                athleteId={competitor.athleteId}
                place={place.pending}
                placeCaption={
                    showPlaceCaption
                        ? place.approved.type === "unscored"
                            ? ""
                            : place.approved.place
                        : ""
                }
                title={hideNames ? "" : competitor.athlete.name}
                caption={caption}
                total={formattedTotal.pending}
                totalCaption={
                    showTotalCaption
                        ? formattedTotal.approved === "-"
                            ? ""
                            : formattedTotal.approved
                        : ""
                }
                tied={tied}
                calloutText={
                    tied && isPendingTie ? (
                        <T>Tie pending approval</T>
                    ) : (
                        <T>Tie</T>
                    )
                }
                calloutIcon={!!pendingRide && <PriorityHigh fontSize="small" />}
                calloutImpact={tied && isPendingTie ? "high" : "medium"}
                jersey={jersey}
                bib={competitor.bib}
                modifier={modifier.pending}
                isPending={!!pendingRide}
                onClick={openApprovalDialog}
            />
        );
    },
);

function getCaption(
    place: ReturnType<typeof usePendingResultChanges>["place"],
) {
    if (place.change === 0) return <T>No rank change</T>;
    if (place.approved.type === "unscored") return <T>Not yet ranked</T>;
    return (
        <>
            {place.change > 0 ? "↓" : "↑"}{" "}
            <T count={Math.abs(place.change)}>spots</T>
        </>
    );
}

function getCorrectedRideIndex(
    ride: PendingLeaderboardRowProps["pendingRide"],
    heat: PendingLeaderboardRowProps["heat"],
) {
    if (!heat.config.runBased) return ride.ride_index;

    // eslint-disable-next-line eqeqeq
    return heat.heats.findIndex((h) => h.id == ride.heat_id);
}
